import { environmentManager, type QueryFunctionContext, queryOptions } from '@tanstack/react-query';

import type { WagerId } from '../../../schema/wager/entry.js';
import { type IDefinition, InternalError } from '../../domain/entry.js';
import {
  type ExtractActionReturnType,
  type ResultFilter,
  wagerQueryKeys,
  type WagerWorkerMessageMap,
} from '../../lib/entry.js';
import type { getWager } from '../actions/wager/getWager.js';

type WagerData = ExtractActionReturnType<typeof getWager>;

type PostCalculateFn = (
  type: 'calculateBestBetRow',
  payload: WagerWorkerMessageMap['calculateBestBetRow']['payload']
) => Promise<WagerWorkerMessageMap['calculateBestBetRow']['response']>;

const createFetcher =
  (post: PostCalculateFn, definition: IDefinition) =>
  ({
    queryKey: [{ wagerId, result, maxRows }],
    client,
  }: QueryFunctionContext<ReturnType<(typeof wagerQueryKeys)['wagerBestBetRows']>>): Promise<
    WagerWorkerMessageMap['calculateBestBetRow']['response']
  > => {
    const wager = client.getQueryData<WagerData>(wagerQueryKeys.wager(wagerId));

    if (!wager) {
      // TODO: replace with actual error class
      throw new InternalError(`No wager for: ${wagerId}. Please fetch it using useWager`);
    }

    const couponWithKey = {
      // key will ensure calculation of srow is cached in worker
      key: wagerId,
      ...wager.coupon,
    } satisfies WagerWorkerMessageMap['calculateBestBetRow']['payload']['coupon'];

    return post('calculateBestBetRow', {
      coupon: couponWithKey,
      drawResult: result,
      definition: definition.model,
      maxRows,
    });
  };

type QueryKeys = {
  wagerId: WagerId;
  resultSrow: ResultFilter;
  maxRows?: number;
};

type QueryFunctionDependencies = {
  post: PostCalculateFn;
  definition: IDefinition;
};

/**
 * Returns query options for fetching the best bet rows for a given wager and result.
 *
 * @param root0 Query key options
 * @param root0.wagerId The ID of the wager to calculate best bet row on
 * @param root0.resultSrow The result srow filter for best bet rows
 * @param root0.maxRows Optional cap on total number of SROWs returned across all win divisions
 * @param root1 Query function dependencies
 * @param root1.post Function to post calculation requests
 * @param root1.definition The product definition model
 * @returns Query options for use with TanStack React Query
 */
export function bestBetRowsOptions(
  { wagerId, resultSrow, maxRows }: QueryKeys,
  { post, definition }: QueryFunctionDependencies
): ReturnType<
  typeof queryOptions<
    WagerWorkerMessageMap['calculateBestBetRow']['response'],
    Error,
    WagerWorkerMessageMap['calculateBestBetRow']['response'],
    ReturnType<(typeof wagerQueryKeys)['wagerBestBetRows']>
  >
> {
  return queryOptions({
    queryKey: wagerQueryKeys.wagerBestBetRows(wagerId, resultSrow, maxRows),
    queryFn: createFetcher(post, definition),
    enabled: !environmentManager.isServer(),
  });
}
